import { useCommunityContext } from 'providers';
import { useMutation, useQueryClient } from 'react-query';

import { MarketplaceV1 } from '@jebel/constants';

import { USER_PRODUCT_MEMBERSHIPS_QUERY_KEY } from 'features/memberships/hooks';

/**
 * Response type for product purchase request.
 * Contains either the completed product (for one-time) or checkout URL (for recurring).
 */
export interface ProductPurchaseResponse {
  /**
   * The Stripe Checkout URL where the user needs to complete payment (recurring subscriptions).
   */
  redirectTo?: string;

  /**
   * Indicates whether payment is required to complete the purchase.
   */
  requiresPayment: boolean;

  /**
   * The user community product that was created.
   */
  userCommunityProduct: MarketplaceV1.UserCommunityMembership;
}

/**
 * Parameters for the request community product mutation.
 */
export interface RequestCommunityProductParams {
  /**
   * The ID of the user.
   */
  user: string;

  /**
   * The ID of the product.
   */
  product: string;

  /**
   * The input data containing the redirect URL.
   */
  redirectUrl: string;
}

/**
 * React hook to request a community product purchase.
 *
 * Uses React Query to make a POST request to the marketplace API
 * to initiate a product purchase for a user in a community.
 * The community is automatically obtained from the current community context.
 *
 * @returns The mutation object from React Query.
 */
export function useRequestCommunityProduct() {
  const { selectedCommunity } = useCommunityContext();

  const client = useQueryClient();

  return useMutation<ProductPurchaseResponse, Error, RequestCommunityProductParams>({
    mutationFn: async ({ user, product, redirectUrl }) => {
      if (!selectedCommunity?.id) {
        throw new Error('No community selected');
      }

      const url = new URL(
        `/community/${selectedCommunity.id}/user/${user}/product/${product}`,
        process.env.REACT_APP_MARKETPLACE_API_URL,
      );

      const response = await fetch(url.toString(), {
        method: 'POST',
        body: JSON.stringify({ redirectUrl }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to request community product purchase (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as ProductPurchaseResponse;
    },

    async onSuccess(data, variables) {
      // Invalidate the user memberships query to refetch the user's memberships.
      await client.invalidateQueries({
        queryKey: [USER_PRODUCT_MEMBERSHIPS_QUERY_KEY, variables.user],
      });
    },
  });
}
